import {
	ALERT_62,
	EXCEL_ACCEPT,
	EXCEL_RESULT_MESSAGES,
	MAX_ROW_EXCEL,
	NUMBER_OF_CUSTOM_FIELD_APPLICANT,
	NUMBER_OF_CUSTOM_FIELD_FRANCHISE,
	NUMBER_OF_TEMPLATE_APPLICANT,
	NUMBER_OF_TEMPLATE_FRANCHISE,
	OPTIONAL_HEADERS_APPLICANT,
	OPTIONAL_HEADERS_FRANCHISE,
	PageTypeEnum,
	TextMessage,
	TypeTemplateExcelEnum,
} from '@/constant';
import { useToggle } from '@/hooks';
import {
	exportExcelCurrentRecommender,
	getWorkerApplicantList,
	getWorkerAssignmentList,
	getWorkerFranchiseList,
	getWorkerLodgingList,
} from '@/services';
import {
	UnitSettingActions,
	Us1_01UnitSettingSelectors,
	useAppSelector,
	WorkerApplicantActions,
	WorkerApplicantSelectors,
	WorkerAssignmentActions,
	WorkerAssignmentSelectors,
	WorkerFranchiseActions,
	WorkerFranchiseSelectors,
	WorkerLodgingActions,
	WorkerLodgingSelectors,
} from '@/stores';
import { MyPageSelectors } from '@/stores/my-page';
import {
	WorkerRecommenderActions,
	WorkerRecommenderSelectors,
} from '@/stores/worker-recommender';
import {
	convertDataExcelToRequestBody,
	exportDataToExcel,
	getHeaderFromTemplateFile,
	getNameOfTemplateFile,
	getOptionalHeaders,
	getRowHeader,
	getTemplate,
	isFileTypeInvalid,
	NOOP,
	readFileExcel,
	setNameOfFileWhenDownload,
	showAlert,
	validateHeaders,
} from '@/utils';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { DefaultOptionType } from 'antd/es/select';

import { AxiosResponse } from 'axios';
import { saveAs } from 'file-saver';
import { clone, includes } from 'ramda';
import { isNotNilOrEmpty } from 'ramda-adjunct';

import { getScreenData, TriggerUploadExcelClick } from './config';
import { TUploadExcelProps } from './type';

export const useLogic = (props: TUploadExcelProps) => {
	const [openModal, onToggle] = useToggle(false);
	const [files, setFiles] = useState<FileList | null>(null);
	const [fileName, setFilename] = useState<string>('');
	const [customFieldsLabel, setCustomFieldsLabel] = useState<string[]>([]);
	const [customFieldsOptions, setCustomFieldsOptions] = useState<
		DefaultOptionType[]
	>([]);
	const [loading, setLoading] = useState(false);
	const [showErrorFileResult, setShowErrorFileResult] = useState(true);

	const inputRef = useRef<HTMLInputElement>(null);
	const { t } = useTranslation(['common']);
	const { pageType, uploadExcelSuccessCallback } = props;

	const failedListApplicant = useAppSelector(
		WorkerApplicantSelectors.selectFailedList
	);
	const uploadExcelResultApplicant = useAppSelector(
		WorkerApplicantSelectors.selectUploadExcelResult
	);

	const failedListRecommender = useAppSelector(
		WorkerRecommenderSelectors.selectFailedList
	);
	const uploadExcelResultRecommender = useAppSelector(
		WorkerRecommenderSelectors.selectUploadExcelResult
	);

	const failedListLodging = useAppSelector(
		WorkerLodgingSelectors.selectFailedList
	);
	const uploadExcelResultLodging = useAppSelector(
		WorkerLodgingSelectors.selectUploadExcelResult
	);

	const failedListAssignment = useAppSelector(
		WorkerAssignmentSelectors.selectUploadExcelAssignmentFailedList
	);
	const uploadExcelResultAssignment = useAppSelector(
		WorkerAssignmentSelectors.selectUploadExcelAssignmentResult
	);
	const failedListPublicAssignment = useAppSelector(
		WorkerAssignmentSelectors.selectUploadExcelPublicAssignmentFailedList
	);
	const uploadExcelResultPublicAssignment = useAppSelector(
		WorkerAssignmentSelectors.selectUploadExcelPublicAssignmentResult
	);
	const isUploadExcelProgramNameNotFoundAssignment = useAppSelector(
		WorkerAssignmentSelectors.selectIsUploadExcelProgramNameNotFound
	);

	const failedListFranchise = useAppSelector(
		WorkerFranchiseSelectors.selectUploadExcelWorkerFranchiseFailedList
	);
	const uploadExcelResultFranchise = useAppSelector(
		WorkerFranchiseSelectors.selectUploadExcelWorkerFranchiseResult
	);

	const dispatch = useDispatch();

	useEffect(() => {
		if (openModal) {
			setFiles(null);
			setFilename('');
		}
	}, [openModal]);

	useEffect(() => {
		TriggerUploadExcelClick.show = onToggle;
	}, []);

	useEffect(() => {
		if (
			openModal &&
			(pageType === PageTypeEnum.APPLICANT ||
				pageType === PageTypeEnum.FRANCHISE)
		) {
			dispatch(UnitSettingActions.getUnitSettingRequest({}));
		}
	}, [openModal, pageType]);

	const selectUnitSettingMasterInfo = useSelector(
		Us1_01UnitSettingSelectors.selectUnitSettingMasterInfo
	);

	const {
		optionalFieldApplicantList,
		statusListOption,
		districtListOption,
		optionalFieldFranchiseList,
	} = selectUnitSettingMasterInfo;

	const screenData = useMemo(
		() => getScreenData?.[props?.pageType],
		[props.pageType]
	);

	const getCustomFieldHeaders = useMemo(() => {
		switch (props.pageType) {
			case PageTypeEnum.APPLICANT:
				setCustomFieldsOptions(
					optionalFieldApplicantList.map((item) => ({
						label: item.label,
						value: `customField${item.fieldNo}`,
					}))
				);
				return optionalFieldApplicantList?.map((item) => item.label);
			case PageTypeEnum.FRANCHISE:
				setCustomFieldsOptions(
					optionalFieldFranchiseList.map((item) => ({
						label: item.label,
						value: `customField${item.fieldNo}`,
					}))
				);
				return optionalFieldFranchiseList?.map((item) => item.label);
			default:
				return [];
		}
	}, [optionalFieldApplicantList, optionalFieldFranchiseList, props.pageType]);

	const reloadData = () => {
		// Find and click the reset button
		const resetButton = document.querySelector('button[id="reset-button"]');
		if (resetButton instanceof HTMLButtonElement) {
			resetButton.click();
		} else {
			console.error('Reset button not found');
		}

		// Find and click the search button
		const searchButton = document.querySelector('button[id="search-button"]');
		if (searchButton instanceof HTMLButtonElement) {
			searchButton.click();
		} else {
			console.error('Search button not found');
		}

		uploadExcelSuccessCallback?.();
	};

	const onChangeFile = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e?.target?.files;
		if (isFileTypeInvalid(EXCEL_ACCEPT, file?.[0]?.name || '')) {
			return showAlert('허용 확장자의 형식이 잘못되었습니다.');
		}
		setFiles(file);
		setFilename(file?.[0]?.name || '');
	}, []);

	const handleClickButtonUpload = useCallback(() => {
		if (isNotNilOrEmpty(files)) {
			showAlert('최대 1개의 업로드 파일만 등록 할 수 있습니다');
		} else {
			inputRef?.current?.click();
		}
	}, [files, inputRef?.current]);

	const handleDeleteFile = useCallback(() => {
		showAlert(
			TextMessage.ALERT_44,
			() => {
				setFiles(null);
				setFilename('');
				if (inputRef.current) {
					inputRef.current.value = '';
				}
			},
			NOOP
		);
	}, [inputRef.current]);

	const handleDownloadTemplate = useCallback(async () => {
		try {
			setLoading(true);
			const headerRows = getRowHeader(pageType);
			const customFields = getCustomFieldHeaders;
			const blob = await getTemplate(pageType, customFields, headerRows);
			if (!blob) {
				showAlert(TextMessage.ALERT_8);
				return;
			}
			const fileName = setNameOfFileWhenDownload({
				pageType,
				type: TypeTemplateExcelEnum.TEMPLATE,
			});
			saveAs(blob, `${fileName}.xlsx`);
		} catch (error) {
			console.error('Error downloading template:', error);
			showAlert(TextMessage.ALERT_8);
		} finally {
			setLoading(false);
		}
	}, [pageType, getCustomFieldHeaders]);

	const handleCallApiUpload = useCallback(
		async (data: any[], customFields: DefaultOptionType[]) => {
			setLoading(true);

			try {
				const uploadActions = {
					[PageTypeEnum.APPLICANT]:
						WorkerApplicantActions.uploadExcelApplicantRequest,
					[PageTypeEnum.RECOMMENDER]:
						WorkerRecommenderActions.uploadExcelRecommenderRequest,
					[PageTypeEnum.LODGING]:
						WorkerLodgingActions.uploadExcelLodgingRequest,
					[PageTypeEnum.ASSIGNMENT]:
						WorkerAssignmentActions.uploadExcelAssignmentRequest,
					[PageTypeEnum.FRANCHISE]:
						WorkerFranchiseActions.uploadExcelWorkerFranchiseRequest,
					[PageTypeEnum.PUBLIC_ASSIGNMENT]:
						WorkerAssignmentActions.uploadExcelPublicAssignmentRequest,
				};

				const action = uploadActions[pageType as keyof typeof uploadActions];

				if (!action) {
					console.error(`Unsupported page type: ${pageType}`);
					return;
				}

				const handleForUploadExcelAssignment = (
					hasAlert62Arr: any[],
					errorList: any[]
				) => {
					showAlert(
						ALERT_62(hasAlert62Arr?.length),
						() => {
							dispatch(
								action({
									data: hasAlert62Arr,
									customFields,
									hasAlert62: true,
									errorList,
									callback: ({ error, successCount, failCount }) => {
										if (error) {
											showAlert(TextMessage.ALERT_8);
										} else {
											if (successCount > 0) {
												reloadData();
											}
											if (failCount === 0) {
												showAlert('업로드에 성공했습니다.', () => onToggle());
											}
										}
										setShowErrorFileResult(true);
									},
								})
							);
						},
						() => {
							setShowErrorFileResult(true);
						}
					);
				};

				const handleForUploadExcelApplicant = (
					hasAlert62Arr: any[],
					errorList: any[]
				) => {
					showAlert(
						'중도 출국 값을 입력한 근로자 중 출국 일자 이 후에 남아 있는 공공형 배정 정보가 있습니다. 남아 있는 배정 정보를 삭제하시겠습니까?',
						() => {
							dispatch(
								action({
									data: hasAlert62Arr,
									customFields,
									hasAlert62: true,
									errorList,
									callback: ({ error, successCount, failCount }) => {
										if (error) {
											showAlert(TextMessage.ALERT_8);
										} else {
											if (successCount > 0) {
												reloadData();
											}
											if (failCount === 0) {
												showAlert('업로드에 성공했습니다.', () => onToggle());
											}
										}
										setShowErrorFileResult(true);
									},
								})
							);
						},
						() => {
							setShowErrorFileResult(true);
						}
					);
				};

				await new Promise<void>((resolve) => {
					dispatch(
						action({
							data,
							customFields,
							callback({
								error,
								successCount,
								failCount,
								hasAlert62Arr,
								errorList,
							}) {
								if (error) {
									showAlert(TextMessage.ALERT_8);
								} else {
									if (
										pageType === PageTypeEnum.ASSIGNMENT &&
										hasAlert62Arr?.length > 0
									) {
										setShowErrorFileResult(false);
										handleForUploadExcelAssignment(hasAlert62Arr, errorList);
									}
									if (
										pageType === PageTypeEnum.APPLICANT &&
										hasAlert62Arr?.length > 0
									) {
										setShowErrorFileResult(false);
										handleForUploadExcelApplicant(hasAlert62Arr, errorList);
									}

									if (successCount > 0) {
										reloadData();
									}
									if (failCount === 0) {
										showAlert('업로드에 성공했습니다.', () => onToggle());
									}
								}
								resolve();
							},
						})
					);
				});
			} finally {
				setLoading(false);
			}
		},
		[pageType, dispatch, reloadData, onToggle]
	);

	const handleUploadFile = useCallback(async () => {
		if (!files) {
			// onToggle();
			showAlert(TextMessage.ALERT_9);
			return;
		}

		try {
			setLoading(true);
			const rowHeader = getRowHeader(pageType);
			const { data, headers } = await readFileExcel({
				file: files[0],
				rowHeader,
				pageType,
			});
			const headersTemplate = await getHeaderFromTemplateFile(pageType);
			const allCustomFieldHeaders = getCustomFieldHeaders;
			let headerWithOutCustomField: string[] = headers;

			if (pageType === PageTypeEnum.APPLICANT) {
				headerWithOutCustomField = headers.slice(
					0,
					NUMBER_OF_TEMPLATE_APPLICANT
				);
			} else if (pageType === PageTypeEnum.FRANCHISE) {
				headerWithOutCustomField = headers.slice(
					0,
					NUMBER_OF_TEMPLATE_FRANCHISE
				);
			}

			setCustomFieldsLabel(allCustomFieldHeaders);

			const optionalHeaders = getOptionalHeaders(pageType).concat(
				allCustomFieldHeaders
			);

			let fieldsCustomOfFile: string[] = [];
			if (pageType === PageTypeEnum.APPLICANT) {
				fieldsCustomOfFile = headers
					.slice(
						NUMBER_OF_TEMPLATE_APPLICANT,
						NUMBER_OF_TEMPLATE_APPLICANT + NUMBER_OF_CUSTOM_FIELD_APPLICANT
					)
					?.filter((x) => !OPTIONAL_HEADERS_APPLICANT?.includes(x));
			} else if (pageType === PageTypeEnum.FRANCHISE) {
				fieldsCustomOfFile = headers
					.slice(
						NUMBER_OF_TEMPLATE_FRANCHISE,
						NUMBER_OF_TEMPLATE_FRANCHISE + NUMBER_OF_CUSTOM_FIELD_FRANCHISE
					)
					?.filter((x) => !OPTIONAL_HEADERS_FRANCHISE?.includes(x));
			}

			const isValidCustomField = fieldsCustomOfFile.every((field) =>
				allCustomFieldHeaders.includes(field)
			);

			const isValidFile = validateHeaders({
				headers: headerWithOutCustomField,
				headersTemplate,
				optionalHeaders,
			});

			if (!isValidFile) {
				showAlert(
					'엑셀 형식이 올바르지 않습니다. \n 엑셀 양식을 확인해주세요.'
				);
				return;
			}

			if (!isValidCustomField) {
				showAlert(EXCEL_RESULT_MESSAGES.EXCEL_RESULT_ALL_4);
				return;
			}

			if (!data?.length) {
				showAlert(
					'엑셀 데이터가 올바르지 않습니다. \n엑셀 양식을 확인 부탁드립니다.'
				);
				return;
			}

			if (data.length > MAX_ROW_EXCEL) {
				showAlert(`최대 ${MAX_ROW_EXCEL}개 등록 가능합니다`);
				return;
			}

			const uploadData = convertDataExcelToRequestBody({
				parsedData: data,
				pageType,
				option: {
					districtListOption,
					statusListOption,
					optionalFieldApplicantList,
					optionalFieldFranchiseList,
				},
			});

			if (uploadData)
				await handleCallApiUpload(uploadData, customFieldsOptions);
		} catch (error) {
			console.error('Error uploading file:', error);
			showAlert(TextMessage.ALERT_8);
		} finally {
			setLoading(false);
		}
	}, [
		onToggle,
		files,
		pageType,
		getCustomFieldHeaders,
		districtListOption,
		statusListOption,
		optionalFieldApplicantList,
		optionalFieldFranchiseList,
		handleCallApiUpload,
		customFieldsOptions,
	]);

	const hasFailedList = useMemo(() => {
		const failedLists = {
			[PageTypeEnum.APPLICANT]: failedListApplicant,
			[PageTypeEnum.RECOMMENDER]: failedListRecommender,
			[PageTypeEnum.LODGING]: failedListLodging,
			[PageTypeEnum.ASSIGNMENT]: failedListAssignment,
			[PageTypeEnum.FRANCHISE]: failedListFranchise,
			[PageTypeEnum.PUBLIC_ASSIGNMENT]: failedListPublicAssignment,
		};
		return (failedLists[pageType as keyof typeof failedLists]?.length ?? 0) > 0;
	}, [
		pageType,
		failedListApplicant,
		failedListRecommender,
		failedListLodging,
		failedListAssignment,
		failedListFranchise,
		failedListPublicAssignment,
	]);

	const getUploadExcelResult = useMemo(() => {
		const uploadExcelResults = {
			[PageTypeEnum.APPLICANT]: uploadExcelResultApplicant,
			[PageTypeEnum.LODGING]: uploadExcelResultLodging,
			[PageTypeEnum.RECOMMENDER]: uploadExcelResultRecommender,
			[PageTypeEnum.ASSIGNMENT]: uploadExcelResultAssignment,
			[PageTypeEnum.FRANCHISE]: uploadExcelResultFranchise,
			[PageTypeEnum.PUBLIC_ASSIGNMENT]: uploadExcelResultPublicAssignment,
		};
		return uploadExcelResults[pageType as keyof typeof uploadExcelResults];
	}, [
		pageType,
		uploadExcelResultApplicant,
		uploadExcelResultLodging,
		uploadExcelResultRecommender,
		uploadExcelResultAssignment,
		uploadExcelResultFranchise,
		uploadExcelResultPublicAssignment,
	]);

	const handleDownloadFailedData = useCallback(async () => {
		setLoading(true);
		let data: any[] = [];
		switch (pageType) {
			case PageTypeEnum.APPLICANT:
				data = clone(failedListApplicant);
				break;
			case PageTypeEnum.RECOMMENDER:
				data = clone(failedListRecommender);
				break;
			case PageTypeEnum.LODGING:
				data = clone(failedListLodging);
				break;
			case PageTypeEnum.ASSIGNMENT:
				data = clone(failedListAssignment);
				break;
			case PageTypeEnum.FRANCHISE:
				data = clone(failedListFranchise);
				break;
			case PageTypeEnum.PUBLIC_ASSIGNMENT:
				data = clone(failedListPublicAssignment);
				break;
			default:
				break;
		}

		const fileName =
			setNameOfFileWhenDownload({
				pageType,
				type: TypeTemplateExcelEnum.FAILED_DATA,
			}) || '';

		const templateName = getNameOfTemplateFile({
			pageType,
			type: TypeTemplateExcelEnum.FAILED_DATA,
		});

		await exportDataToExcel({
			filename: fileName,
			templateName,
			data,
			customFields:
				pageType === PageTypeEnum.APPLICANT ||
				pageType === PageTypeEnum.FRANCHISE
					? customFieldsLabel
					: [],
			headerRows: getRowHeader(pageType),
			optionalFieldApplicantList:
				pageType === PageTypeEnum.APPLICANT ? optionalFieldApplicantList : [],
			optionalFieldFranchiseList:
				pageType === PageTypeEnum.FRANCHISE ? optionalFieldFranchiseList : [],
			pageType,
			type: 'failedData',
			additionalAssignment: {
				isProgramNameNotFound: includes(pageType, [
					PageTypeEnum.ASSIGNMENT,
					// PageTypeEnum.PUBLIC_ASSIGNMENT,
				])
					? isUploadExcelProgramNameNotFoundAssignment
					: false,
			},
			t,
		});
		setLoading(false);
	}, [
		pageType,
		failedListApplicant,
		failedListLodging,
		customFieldsLabel,
		optionalFieldApplicantList,
		optionalFieldFranchiseList,
		failedListRecommender,
		failedListAssignment,
		failedListFranchise,
		failedListPublicAssignment,
		t,
	]);

	// prettier-ignore
	const callApiDownloadCurrentData = useCallback( async (reason: string, fileName?: string) => {
		let data: any[] = [];
		let response: AxiosResponse = {} as AxiosResponse;
		setLoading(true);
		try {
			switch (pageType) {
				case PageTypeEnum.APPLICANT:
					response = await getWorkerApplicantList({
						page: 1,
						pageSize: 10,
						allData: true,
						exportExcel: true,
						downloadReason: reason,
						downloadFileName: fileName,
					});
					if (response?.status === 200 && response?.data?.content) {
						data = response?.data?.content;
					}
					break;
				case PageTypeEnum.FRANCHISE:
					response = await getWorkerFranchiseList({
						page: 1,
						pageSize: 10,
						allData: true,
						exportExcel: true,
						downloadReason: reason,
						downloadFileName: fileName,
					});
					if (response?.status === 200 && response?.data?.content) {
						data = response?.data?.content;
					}
					break;
				case PageTypeEnum.RECOMMENDER:
					response = await exportExcelCurrentRecommender(reason, fileName);
					if (response?.status === 200 && response?.data) {
						data = response?.data;
					}
					break;
				case PageTypeEnum.LODGING:
					response = await getWorkerLodgingList({
						page: 1,
						pageSize: 10,
						allData: true,
						exportExcel: true,
						downloadReason: reason,
						downloadFileName: fileName,
					});
					if (response?.status === 200 && response?.data?.content) {
						data = response?.data?.content;
					}
					break;
				case PageTypeEnum.ASSIGNMENT:
				case PageTypeEnum.PUBLIC_ASSIGNMENT:
					response = await getWorkerAssignmentList({
						page: 1,
						pageSize: 10,
						allData: true,
						exportExcel: true,
						downloadReason: reason,
						downloadFileName: fileName,
					});
					if (response?.status === 200 && response?.data?.content) {
						data = response?.data?.content;
					}
					break;

				default:
					break;
			}

			return data;
		} catch (error) {
			showAlert(TextMessage.ALERT_8);
			throw error;
		} finally {
			setLoading(false);
		}
	}, [pageType]);

	// prettier-ignore
	const handleDownloadCurrentData = useCallback(async (reason: string, callback?: () => void) => {
		try {
			const fileName =
				setNameOfFileWhenDownload({
					pageType,
					type: TypeTemplateExcelEnum.CURRENT_DATA,
				}) || '';

			const templateName = getNameOfTemplateFile({
				pageType,
				type: TypeTemplateExcelEnum.TEMPLATE,
			});

			const fileNameWithExtension = `${fileName}.xlsx`;
			let data: any[] = await callApiDownloadCurrentData(reason, fileNameWithExtension);

			const allCustomFields = getCustomFieldHeaders;

			await exportDataToExcel({
				filename: fileName,
				templateName,
				data,
				customFields:
					pageType === PageTypeEnum.APPLICANT ||
					pageType === PageTypeEnum.FRANCHISE
						? allCustomFields
						: [],
				headerRows: getRowHeader(pageType),
				optionalFieldApplicantList:
					pageType === PageTypeEnum.APPLICANT ? optionalFieldApplicantList : [],
				optionalFieldFranchiseList:
					pageType === PageTypeEnum.FRANCHISE ? optionalFieldFranchiseList : [],
				pageType,
				type: 'currentData',
				t,
			});
		} catch (error) {
			console.warn(error);
		} finally {
			callback?.();
		}
	}, [
		pageType,
		customFieldsLabel,
		optionalFieldApplicantList,
		optionalFieldFranchiseList,
		getCustomFieldHeaders,
		t,
	]);

	const { isWorkerPublicAdminRole } = useSelector(
		MyPageSelectors.selectDataMyPage
	);

	const disabledUploadExcel = useMemo(() => {
		return isWorkerPublicAdminRole && pageType === PageTypeEnum.ASSIGNMENT;
	}, [isWorkerPublicAdminRole, pageType]);

	return {
		screenData,
		openModal,
		onToggle,
		inputRef,
		onChangeFile,
		handleClickButtonUpload,
		files,
		fileName,
		setFiles,
		handleUploadFile,
		handleDeleteFile,
		handleDownloadTemplate,
		hasFailedList,
		handleDownloadFailedData,
		handleDownloadCurrentData,
		loading,
		getUploadExcelResult,
		disabledUploadExcel,
		showErrorFileResult,
	};
};
